import { handleFrom } from 'Helpers/dates'
import { storeGet } from 'Helpers/localStorage'
import { fromUTCtoZoneFormatted, STORE_KEY_LIST } from 'mailup-react-helpers'
import {
  Checkbox,
  Chip,
  DatePicker,
  DatePickerButton,
  Divider,
  Flex,
  FlexItem,
  Grid,
  GridCell,
  Panel,
  SPACING_24,
  SPACING_4,
  SpacingVertical,
  Text,
  TEXT_VARIANT_BOLD,
  Title,
  TITLE_VARIANT_M
} from 'mailup-web-components'
import { step4State } from 'Pages/Workflow/recoil/state/step4'

import { useIntl } from 'react-intl'
import { useRecoilState } from 'recoil'

const addTimezone = (date) => {
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)
  return handleFrom(date, TimeZoneCode)
}

const DateRestriction = () => {
  const intl = useIntl()
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)

  const [
    { StartDate, EndDate, NotInWeekends, FrequencyBlackoutDate },
    setWorkflowDetails
  ] = useRecoilState(step4State)

  const handleStartChange = (value) => {
    setWorkflowDetails((prev) => ({
      ...prev,
      StartDate: value === null ? null : addTimezone(value)
    }))
  }

  const handleEndChange = (value) => {
    setWorkflowDetails((prev) => ({
      ...prev,
      EndDate: value === null ? null : addTimezone(value)
    }))
  }

  const handleWeekends = () => {
    setWorkflowDetails((prev) => ({
      ...prev,
      NotInWeekends: !prev.NotInWeekends
    }))
  }

  const handleBlackoutDates = (value) => {
    if (
      !FrequencyBlackoutDate.some(
        (e) =>
          !e.Recurring &&
          fromUTCtoZoneFormatted(e.Date, TimeZoneCode, 'YYYY-MM-DD') ===
            fromUTCtoZoneFormatted(value, TimeZoneCode, 'YYYY-MM-DD')
      )
    ) {
      setWorkflowDetails((prev) => ({
        ...prev,
        FrequencyBlackoutDate: [
          ...FrequencyBlackoutDate,
          {
            Date: addTimezone(value),
            Recurring: false
          }
        ]
      }))
    }
  }

  const handleRecurringBoDates = (value) => {
    if (
      !FrequencyBlackoutDate.some(
        (e) =>
          e.Recurring &&
          fromUTCtoZoneFormatted(e.Date, TimeZoneCode, 'MM-DD') ===
            fromUTCtoZoneFormatted(value, TimeZoneCode, 'MM-DD')
      )
    ) {
      setWorkflowDetails((prev) => ({
        ...prev,
        FrequencyBlackoutDate: [
          ...FrequencyBlackoutDate,
          {
            Date: addTimezone(value),
            Recurring: true
          }
        ]
      }))
    }
  }

  const handleChipClick = (e) => {
    const filteredDates = FrequencyBlackoutDate.filter((i) => e !== i)

    setWorkflowDetails((prev) => ({
      ...prev,
      FrequencyBlackoutDate: filteredDates
    }))
  }

  const today = new Date()
  today.setHours(0, 0, 0)

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0)

  const startDateAsDate = StartDate ? new Date(StartDate) : null
  const endDateAsDate = EndDate ? new Date(EndDate) : null

  const minFromDate =
    startDateAsDate && startDateAsDate.getTime() < today.getTime()
      ? startDateAsDate
      : today

  const minToDate = startDateAsDate || tomorrow

  return (
    <Panel>
      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage({ id: 'Load.AutomationWorkflow.RestrictionTitle' })}
      </Title>
      <SpacingVertical variant={SPACING_24} />
      <Grid cols={12}>
        <GridCell span={2}>
          <Flex height='100%' alignItems='center'>
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage({
                id: 'Load.AutomationWorkflow.RestrictionActiveFrom'
              })}
            </Text>
          </Flex>
        </GridCell>
        <GridCell span={10}>
          <Flex columnGap='8px' height='100%' alignItems='center'>
            <DatePicker
              value={StartDate ? new Date(StartDate) : null}
              callback={handleStartChange}
              minDate={minFromDate}
              maxDate={endDateAsDate}
            />
            <Text>
              {intl.formatMessage({
                id: 'Load.AutomationWorkflow.RestrictionActiveTo'
              })}
            </Text>
            <DatePicker
              value={EndDate ? new Date(EndDate) : null}
              callback={handleEndChange}
              minDate={minToDate}
            />
          </Flex>
        </GridCell>
      </Grid>
      <SpacingVertical variant={SPACING_24} />
      <Grid cols={12}>
        <GridCell span={2}>
          <Flex margin='8px 0 0 0'>
            <Text htmlElement='span' variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage({
                id: 'Load.AutomationWorkflow.RestrictionMinus'
              })}
            </Text>
          </Flex>
        </GridCell>
        <GridCell span={10}>
          <Flex flexDirection='column' rowGap='16px'>
            <Grid cols={12}>
              <GridCell span={4}>
                <Flex margin='8px' justifyContent='flex-start'>
                  <Text>
                    {intl.formatMessage({
                      id: 'Load.AutomationWorkflow.RestrictionBlackout'
                    })}
                  </Text>
                </Flex>
              </GridCell>
              <GridCell span={8}>
                <Flex>
                  <DatePickerButton
                    onChange={handleBlackoutDates}
                    minDate={minFromDate}
                  />
                  <Flex margin='0 0 0 24px' gap='8px' flexWrap='wrap'>
                    {FrequencyBlackoutDate.filter((e) => !e.Recurring).map(
                      (e, index) => (
                        <FlexItem key={index}>
                          <Chip
                            icon='times'
                            label={fromUTCtoZoneFormatted(
                              e.Date,
                              TimeZoneCode,
                              'YYYY-MM-DD'
                            )}
                            value={e}
                            onClick={handleChipClick}
                          />
                        </FlexItem>
                      )
                    )}
                  </Flex>
                </Flex>
              </GridCell>
            </Grid>
            <FlexItem>
              <Divider />
              <SpacingVertical variant={SPACING_4} />
            </FlexItem>
            <Grid cols={12}>
              <GridCell span={4}>
                <Flex margin='8px' justifyContent='flex-start'>
                  <Text>
                    {intl.formatMessage({
                      id: 'Load.AutomationWorkflow.RestrictionBlackoutRecurring'
                    })}
                  </Text>
                </Flex>
              </GridCell>
              <GridCell span={8}>
                <Flex>
                  <DatePickerButton onChange={handleRecurringBoDates} />
                  <Flex margin='0 0 0 24px' gap='8px' flexWrap='wrap'>
                    {FrequencyBlackoutDate.filter((e) => e.Recurring).map(
                      (e, index) => (
                        <FlexItem key={index}>
                          <Chip
                            icon='times'
                            label={fromUTCtoZoneFormatted(
                              e.Date,
                              TimeZoneCode,
                              'MM-DD'
                            )}
                            value={e}
                            onClick={handleChipClick}
                          />
                        </FlexItem>
                      )
                    )}
                  </Flex>
                </Flex>
              </GridCell>
            </Grid>
            <FlexItem>
              <Divider />
              <SpacingVertical variant={SPACING_4} />
            </FlexItem>
            <FlexItem>
              <Checkbox
                checked={NotInWeekends}
                onChange={handleWeekends}
                label={intl.formatMessage({
                  id: 'Load.AutomationWorkflow.RestrictionWeekend'
                })}
              />
            </FlexItem>
          </Flex>
        </GridCell>
      </Grid>
    </Panel>
  )
}

export default DateRestriction
